
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { ProjectForm } from '../../../presentation-components/organisation-components/project-form'
import { useContext, useEffect, useState } from 'react';
import ProjectService from '../../../../shared/services/project/project.service';
import { IPostProject, IProject } from '../../../../shared/services/project/project.model';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { a11yProps, deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog';
import { LoadingPage } from '../../loading-page';
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import ProjectReportSettingService from '../../../../shared/services/project-report-setting/project-report-setting.service';
import type { IProjectReportSetting } from '../../../../shared/services/project-report-setting/project-report-setting.model';
import FileDownloadService from '../../../../shared/services/file-download/file-download.service';
import { IFileDownload } from '../../../../shared/services/file-download/file-download.model';
import { ReportingProjectWebPageSettingsForm } from '../../../presentation-components/reporting-components/project/reporting-project-web-page-settings-form';
import { ReportContentEditor } from '../../../presentation-components/reporting-components/report-content-editor';
import { PROJECT_REPORT_CONTENT } from '../../../../modules/public-reports/shared-services/gaia-report-content-schema';
import { ReportingProjectDownloadPdfTable } from '../../../presentation-components/reporting-components/project/reporting-project-download-pdf-table';

export const OrganisationProjectEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  //Services
  const projectService = ProjectService.getInstance()
  const projectReportSettingService = new ProjectReportSettingService()
  const fileDownloadService = new FileDownloadService()
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  //State
  const [value, setValue] = useState(0)
  const [project, setProject] = useState<IProject>();
  const [editField, setEditField] = useState<string>('')
  const [hasOpenReport, setHasOpenReport] = useState<boolean>(true)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>()
  const [errorCode, setErrorCode] = useState<number>()
  const [reportSettings, setReportSettings] = useState<IProjectReportSetting | null>(null)
  const [reportSubTab, setReportSubTab] = useState(0)
  const [fileDownloads, setFileDownloads] = useState<IFileDownload[]>([])
  const [downloadsPage, setDownloadsPage] = useState(0)
  const [downloadsTotalRecords, setDownloadsTotalRecords] = useState(0)
  const [downloadsPerPage, setDownloadsPerPage] = useState(10)
  //Loading State
  const [isFormSubmitting, setIsFormSubitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Use Effects
  useEffect(() => {
    if (searchParams.has('tabId')) {
      const token = searchParams.get('tabId')
      if (token) {
        setValue(parseInt(token))
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
  }, [])

  useEffect(() => {
    if (id && value === 0) {
      getProject(id)
    } else if (id && value === 2) {
      getReportSettings(id)
      getDownloads(id)
    }
  }, [reportPeriodContext.lastSelectedReportId, id, value, downloadsPage, downloadsPerPage])


  const getProject = (id: string) => {
    projectService.getOne(id)
      .then(response => {
        setProject(response)
        setIsLoading(false)
      })
      .catch(err => {
        console.log(err)
        setIsLoading(false)
      })
  }

  const getReportSettings = (projectId: string) => {
    projectReportSettingService.getSettings(projectId)
      .then(settings => setReportSettings(settings))
      .catch(err => console.log(err))
  }

  const getDownloads = (projectId: string) => {
    fileDownloadService.getAllByProjectId(projectId, 'project-report', downloadsPage, downloadsPerPage)
      .then(result => {
        setFileDownloads(result.rows)
        setDownloadsTotalRecords(result.count)
      })
      .catch(err => console.log(err))
  }

  function refreshProjects(): void {
    projectService.getAll(undefined, true)
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
      })
      .catch(err => {
        console.log(err)
      })
  }

  const submitHandler = async (editProject: IPostProject) => {
    if (id && editProject) {
      if (!isFormSubmitting) {
        setIsFormSubitting(true)
        projectService.update(id, editProject)
          .then(response => {
            getProject(response.id!)
            setEditField('')
            refreshProjects();
            setIsFormSubitting(false)
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            }
            setIsFormSubitting(false)
            console.log(err)
          })
      }
    }
  }

  const deleteHandler = (id: string, isInUse: boolean) => {
    if (isInUse) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Required',
        message: `You are unable to delete this project as it has or currently is in use. Please review and update any associated emissions before attempting to delete it.`,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this ${screenLabelsContext.getLabel('project', { plural: false })}? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            if (!isFormSubmitting) {
              setIsFormSubitting(true)
              projectService.remove(id)
                .then(() => {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
                  navigate('/organisation/projects')
                  setIsFormSubitting(false)
                  refreshProjects()
                })
                .catch(err => {
                  if (err.response.data.code === 1) {
                    userNotificationsContext.openDialog({
                      active: true, title: 'Action Not Allowed',
                      message: `This ${screenLabelsContext.getLabel('project', { plural: false })} cannot be deleted because it is currently in use, or has been used previously.`,
                      buttonCopy: 'Ok',
                      hideCancelButton: true
                    })
                  } else {
                    userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  }
                  setIsFormSubitting(false)
                  console.log(err)
                })
            }
          }
        })
    }
  }

  const deactivateHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `By deactivating this ${screenLabelsContext.getLabel('project', { plural: false })}, you will not be able to add any more emissions to the ${screenLabelsContext.getLabel('project', { plural: false })}. This action cannot be undone.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response) {
          if (!isFormSubmitting) {
            setIsFormSubitting(true)
            projectService.deactivate(id)
              .then(response => {
                getProject(id)
                userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
                setIsFormSubitting(false)
                refreshProjects()
              })
              .catch(err => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                console.log(err)
                setIsFormSubitting(false)
              })
          }
        }
      })
  }

  // Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 0) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  };

  const saveProjectReportField = async (field: string, value: any) => {
    if (!id) return;
    const base = reportSettings || ({ projectId: id } as any);
    const updated = { ...base, [field]: value };
    try {
      const saved = await projectReportSettingService.updateSettings(id, updated);
      setReportSettings(saved);
      userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' });
      return saved;
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: '#70787A' });
      return null;
    }
  };

  return (
    <PageOuterContainer>
      {project &&
        <>
          <PageHeading
            title={project.summary}
            includeBackButton={true}
            backButtonLink='/organisation/projects'
            backButtonText={`Back to ${screenLabelsContext.getLabel('project', { plural: true })}`}
            hasTabs={true}
            includeActionButton={true}
            actionButtonIcon={IconName.HideSourceOutlined}
            actionButtonHandler={() => deactivateHandler(project.id)}
            actionButtonText='Deactivate'
            hideShowActionButton={!project.active}
            includeSecondaryActionButton={true}
            secondaryActionButtonIcon={IconName.DeleteOutlineOutlined}
            secondaryActionButtonHandler={() => deleteHandler(project.id, project.projectInUse)}
            secondaryActionButtonText='Delete'
          />
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={changeTabHandler}>
              <Tab label='Details'  {...a11yProps(0)} sx={{ borderRadius: 1 }} />
              <Tab label='Emissions' {...a11yProps(1)} sx={{ borderRadius: 1 }} />
              <Tab label='Report' {...a11yProps(2)} sx={{ borderRadius: 1 }} />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <ProjectForm
              isEditPage={true}
              editField={editField}
              setEditField={setEditField}
              submitHandler={submitHandler}
              newProject={project}
              projectActive={project.active}
              errorMessage={errorMessage}
              errorCode={errorCode}
              setErrorMessage={setErrorMessage}
              setErrorCode={setErrorCode}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {!hasOpenReport &&
              <Box>
                < ClosedReportPeriodMessage openDialog={() => { setDialogOpen(true) }} />
                {dialogOpen && <StartNewReportPeriodDialog open={dialogOpen} closeHandler={() => { setDialogOpen(false) }} />}
              </Box>
            }
            {hasOpenReport &&
              <LoadingPage pageReady={!isLoading}>
                {reportPeriodContext.lastSelectedReportId && id && (
                  <Emissions
                    includeDelete={false}
                    includeEdit={false}
                    queryString={`&projectId=${id}`}
                    backLink={`/organisation/projects/${id}?tabId=1`}
                    backText='Back to project'
                  />
                )}
              </LoadingPage>}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            {id && reportSettings && (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={reportSubTab} onChange={(_, v) => setReportSubTab(v)} aria-label='project report tabs'>
                    <Tab label='Content' {...a11yProps(0)} />
                    <Tab label='Design' {...a11yProps(1)} />
                    <Tab label='PDF Downloads' {...a11yProps(2)} />
                  </Tabs>
                </Box>
                <CustomTabPanel value={reportSubTab} index={0}>
                  <ReportContentEditor
                    areas={PROJECT_REPORT_CONTENT}
                    settings={reportSettings}
                    saveField={saveProjectReportField}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={reportSubTab} index={1}>
                  <ReportingProjectWebPageSettingsForm
                    settings={reportSettings}
                    setSettings={setReportSettings}
                    projectId={id}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={reportSubTab} index={2}>
                  <ReportingProjectDownloadPdfTable
                    fileDownloads={fileDownloads}
                    getDownloads={() => id && getDownloads(id)}
                    projectId={id}
                    inputPage={downloadsPage}
                    setInputPage={setDownloadsPage}
                    totalRecords={downloadsTotalRecords}
                    perPage={downloadsPerPage}
                    setPerPage={setDownloadsPerPage}
                  />
                </CustomTabPanel>
              </>
            )}
          </CustomTabPanel>
        </>}
    </PageOuterContainer>
  )
}